// The "react to this message" affordance: a small round trigger that opens the
// six allowed emoji (Backend/config/chatReactions.js, mirrored in chatShared.js).
//
// Declared at MODULE level and rendered by ChatMessageBubble — never defined
// inside a component. An inline component is a new type on every render, so
// React remounts its subtree and the composer below loses focus after a single
// keystroke. This codebase has shipped that bug more than once.
//
// It matches the MOC patient web app's components/chat/MessageReactionPicker.jsx
// button for button; the two apps cannot import from each other, so "the same
// design on both sides" is kept by these two files being edited together.

import React, { useEffect, useRef, useState } from 'react';
import { SmilePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { REACTION_EMOJIS } from './chatShared';

const MessageReactionPicker = ({ onSelect, selected, align = 'left', testId = 'message-reaction-picker' }) => {
    const { t } = useTranslation(['chat']);
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    // Close on an outside click or Escape. Without the Escape branch the only
    // way off a keyboard is to tab out of the popover, which on a long thread
    // walks the focus ring through every message below it.
    useEffect(() => {
        if (!isOpen) return undefined;
        const onPointerDown = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
        };
        const onKeyDown = (e) => { if (e.key === 'Escape') setIsOpen(false); };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isOpen]);

    return (
        <div className="relative" ref={containerRef} data-testid={`${testId}-container`}>
            <button
                type="button"
                onClick={() => setIsOpen((open) => !open)}
                className={`p-1.5 border rounded-full shadow-sm transition-all cursor-pointer ${selected
                    ? 'bg-[#4ABDBD]/10 border-[#4ABDBD]/40 text-[#2D8A8A]'
                    : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-500'}`}
                title={t('chat.react', 'React')}
                aria-label={t('chat.react', 'React')}
                aria-expanded={isOpen}
                data-testid={`${testId}-toggle`}
            >
                <SmilePlus size={13} />
            </button>

            {isOpen && (
                <div
                    className={`absolute bottom-full mb-2 ${align === 'right' ? 'right-0' : 'left-0'} z-50 bg-white rounded-2xl shadow-xl border border-gray-200 p-1.5 flex gap-0.5`}
                    role="menu"
                    data-testid={`${testId}-popover`}
                >
                    {REACTION_EMOJIS.map((emoji) => (
                        <button
                            key={emoji}
                            type="button"
                            onClick={() => { setIsOpen(false); onSelect(emoji); }}
                            className={`w-8 h-8 flex items-center justify-center text-lg rounded-lg transition-transform hover:scale-125 cursor-pointer ${emoji === selected ? 'bg-[#4ABDBD]/15' : 'hover:bg-gray-100'}`}
                            title={emoji}
                            aria-label={`${t('chat.react_with', 'React with')} ${emoji}`}
                            data-testid={`${testId}-option-${emoji}`}
                        >
                            {emoji}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MessageReactionPicker;
